import {breadcrumbsWithoutName, htmlEscape} from "@/shared/utils/string";
import {
	AddProjectTimeEvent,
	DuplicateProjectTimeEvent,
	EditProjectTimeEvent,
	ProjectTimeItem,
	ProjectTimeTimelineItem,
	ProjectTimeTimelineItemType,
	QuickSwitchProjectTimeEvent,
	ShowProjectTimeEvent,
	ShowTaskDetailsEvent,
	StatusUpdatedEvent,
	TimelineSubitemsComponentConfig,
	TimelineSubitemsContent,
	WorkingTimeTimelineItem,
	WorkingTimeTimelineItemType
} from "../timelineTypes";
import {TimelineContextMenu} from "../timelineContextMenu";
import {
	buildContextMenu,
	buildCustomFields,
	buildDescriptionElement,
	buildTimelineTimes,
	buildValidationsTable,
	deleteProjectTime,
	getQuickSwitchProjectTimeProcedure,
	isProjectTimeDeletable,
	isProjectTimeDuplicateable,
	isProjectTimeEditable,
	isProjectTimeQuickSwitchable,
	isProjectTimeResumable,
	isProjectTimeStatusNumberEnabled,
	resumeProjectTime,
	setProjectTimeStatus,
	showProjectTimeQuickSwitchMessage,
	showProjectTimeResumeBothMessage
} from "../timelineHelper";
import {showProjectTimeBulkResult} from "@/shared/bulkOperationHelper";
import {
	EVENT_PROJECT_TIME_ADD,
	EVENT_PROJECT_TIME_DELETED,
	EVENT_PROJECT_TIME_DUPLICATE,
	EVENT_PROJECT_TIME_EDIT,
	EVENT_PROJECT_TIME_QUICK_SWITCH,
	EVENT_PROJECT_TIME_RESUMED,
	EVENT_PROJECT_TIME_SHOW,
	EVENT_PROJECT_TIME_SHOW_TASK_DETAILS,
	EVENT_PROJECT_TIME_STATUS_UPDATED
} from "../timelineConstants";
import {isProjectTimeDateTimeChangeAllowed, isValidationRelevant, shouldAddStatusNumber} from "@/shared/securityHelper";
import {minutesToMillis} from "@/shared/utils/misc";
import {formatDuration, formatRunningDurationSince, parseISOZonedDateTime, Temporal} from "@/shared/utils/datefunctions";
import {
	CombinedRecordingStatus,
	PartialWorkingTimeType,
	ProjectTime,
	ProjectTimeBulkOperationResult,
	RunningProjectTime
} from "@/shared/timr";
import {DeletedStatus, RunningTimeStatus, TimelineMode, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {toRecordedEntryStatus} from "@/shared/utils/recordMapper";

/**
 * Custom web component that draws subitems of a timeline item
 * These subitems belong to a working time item and may contain the following elements:
 * - RUNNING: running working time
 * - PROJECT_TIME: displays project time, click opens details dialog
 * - GAP: grey panel with duration and break info, click on "+" button opens add dialog
 * - OVERLAP: grey panel with overlap info
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class CombinedTimelineSubitems extends HTMLElement {

	private _content: TimelineSubitemsContent;
	private _config: TimelineSubitemsComponentConfig;
	private _combinedRecordingStatus: CombinedRecordingStatus;

	set content(content: TimelineSubitemsContent) {
		this._content = content;
	}

	get content(): TimelineSubitemsContent {
		return this._content;
	}

	get config(): TimelineSubitemsComponentConfig {
		return this._config;
	}

	set config(value: TimelineSubitemsComponentConfig) {
		this._config = value;
	}

	set combinedRecordingStatus(value: CombinedRecordingStatus) {
		this._combinedRecordingStatus = value;
	}

	get combinedRecordingStatus(): CombinedRecordingStatus {
		return this._combinedRecordingStatus;
	}

	/**
	 * Main entry point to render the subitems of a timeline item.
	 * @protected
	 */
	protected connectedCallback(): void {
		this.innerHTML = `<div data-subitems class="subitems"></div>`;

		// if no subitems exist, nothing to draw
		if (this.content.subitems.length === 0) {
			return;
		}

		// sort items according to configuration (toggled via timeline control button and cached in session variable)
		const orderedSubitems = this.config.timelineSortDescending
			? this.content.subitems.slice().reverse()
			: this.content.subitems;

		const items = orderedSubitems
			.slice() // use slice to copy objects instead of direct mutation, which can lead to problems
			.map(item => {
				switch (item.type) {
					case ProjectTimeTimelineItemType.RUNNING:
						return this.buildRunningItem(item);
					case ProjectTimeTimelineItemType.PROJECT_TIME:
						return this.buildProjectTimeItem(item);
					case ProjectTimeTimelineItemType.GAP:
						return this.buildGapItem(item);
					case ProjectTimeTimelineItemType.OVERLAP:
						return this.buildOverlapItem();
				}
			});

		this.querySelector("div[data-subitems]").append(...items);
	}

	/**
	 * Creates UI element for running project time
	 */
	private buildRunningItem(subitem: ProjectTimeTimelineItem): HTMLDivElement {
		const {runningProjectTime} = subitem;

		// add class for billable state background color
		const div = document.createElement("div");
		div.classList.add("subitem-running", runningProjectTime.billable ? "billable" : "not-billable");
		div.dataset.toggle = "context";

		// escape fields that contain modifiable text to prevent XSS
		const escapedTaskName = htmlEscape(runningProjectTime.task.name);
		const escapedBreadCrumbs = breadcrumbsWithoutName(runningProjectTime.taskBreadCrumbs || "", runningProjectTime.task.name);
		const escapedTaskBreadCrumbs = htmlEscape(runningProjectTime.taskBreadCrumbs || "");

		div.innerHTML =
			`
			${buildTimelineTimes(
				parseISOZonedDateTime(subitem.start),
				null,
				this.content.showTimeZone,
				this.config.timelineSortDescending
			)}
			<!-- running project time -->
			<div class="subitem-project-time-content">
					<div class="subitem-project-time-task" title="${escapedTaskBreadCrumbs}">
					<div>${escapedTaskName}</div>
					<div class="text-breadcrumbs text-nowrap" style="${!escapedBreadCrumbs ? "display: none" : ""}">${escapedBreadCrumbs}</div>
				</div>
				<div class="duration-pulse">
					${runningProjectTime.status === RunningTimeStatus.RUNNING ? `<span class="pulse-recording"></span>` : ""}
					${runningProjectTime.status === RunningTimeStatus.PAUSED ? `<i class="fa fa-pause"></i>` : ""}
				</div>
				<div class="duration-container">
					<span id="runningProjectTimeDuration">${formatRunningDurationSince(runningProjectTime)}</span>
					<div class="info-icons-container">${this.config.projectTimeAttachmentsEnabled && runningProjectTime.attachmentCount > 0 ? "<i class='far fa-image m-0'></i>" : ""}</div>
				</div>
				${buildContextMenu().outerHTML}
			</div>

			<!-- custom fields -->
			${buildCustomFields(this.config.customFieldDefinitionsProjectTime, runningProjectTime).outerHTML}`;

		// add description if available (in weekly mode it is displayed in extra row since the space is limited, in daily in column via flexbox)
		const descriptionElement = buildDescriptionElement(runningProjectTime);
		if (descriptionElement) {
			if (this.config.mode === TimelineMode.Daily) {
				div.querySelector(".subitem-project-time-task").after(descriptionElement);
			} else {
				div.querySelector(".subitem-project-time-content").after(descriptionElement);
			}
		}

		// draw ⊙ dot if start times are equal (project and working time)
		if (subitem.start === this.content.workingTimeItem.start) {
			div.querySelector(".times").classList.add("times-no-start");
		}

		if (subitem.end === this.content.workingTimeItem.end) {
			div.classList.add("subitem-no-end");
		}

		new TimelineContextMenu<RunningProjectTime>()
			.addMenuEntry({
				label: "report.showTaskDetails",
				action: () => this.onShowTaskDetailsClicked(runningProjectTime)
			})
			.bindToElement(div, runningProjectTime);

		return div;
	}

	/**
	 * Creates UI element for project time
	 */
	private buildProjectTimeItem(subitem: ProjectTimeTimelineItem): HTMLDivElement {
		const {projectTime} = subitem;
		const {workingTimeItem} = this.content;

		const div = document.createElement("div");

		// Add CSS classes to style the panel background based on billable and conflict status
		div.classList.add("subitem-project-time", projectTime.billable ? "billable" : "not-billable");
		if (projectTime.conflicted) {
			div.classList.add("conflicted");
		}

		// DEVNOTE: if subitems are outside working times -> inside none item, both start and end times should be visible to avoid displaying incorrect times
		// 			in the future this should be fixed directly by changes in backend timeline service see TIMR-8076
		if (workingTimeItem.type === WorkingTimeTimelineItemType.NONE || workingTimeItem.type === WorkingTimeTimelineItemType.GAP) {
			div.classList.add("subitem-show-both-times");
		}

		// add attribute to element when recording functions are active to apply styling and context menu for personal timeline
		div.dataset.toggle = "context";

		// escape fields that contain modifiable text to prevent XSS
		const escapedTaskName = htmlEscape(projectTime?.task.name);
		const escapedBreadCrumbs = breadcrumbsWithoutName(projectTime.taskBreadCrumbs || "", projectTime.task.name);
		const escapedTaskBreadCrumbs = htmlEscape(projectTime.taskBreadCrumbs || "");

		const positionAvailable =
			projectTime.startPosition?.id && projectTime.startPosition?.deletedStatus === DeletedStatus.NOT_DELETED ||
			projectTime.endPosition?.id && projectTime.endPosition?.deletedStatus === DeletedStatus.NOT_DELETED;

		div.innerHTML =
			`
			${buildTimelineTimes(
				parseISOZonedDateTime(subitem.start),
				parseISOZonedDateTime(subitem.end),
				this.content.showTimeZone,
				this.config.timelineSortDescending
			)}

			<!-- project time -->
			<div class="subitem-project-time-content">
					<div class="subitem-project-time-task" title="${escapedTaskBreadCrumbs}">
					<div>${escapedTaskName}</div>
						<div class="text-breadcrumbs text-nowrap" style="${!escapedBreadCrumbs ? "display: none" : ""}">
						${escapedBreadCrumbs}
					</div>
				</div>
				<div class="breaktime-container" style="${projectTime.breakTime > 0 ? "" : "display: none"}">
					<i class="fal fa-coffee position-left"></i>${formatDuration(minutesToMillis(projectTime.breakTime))}
				</div>
				<div class="duration-container">
					<div class="display-flex">
						${projectTime.changed ? "<i class='fal fa-pen'></i>" : ""}
						${formatDuration(subitem.duration)}
					</div>
					<div class="info-icons-container">
						${this.config.projectTimeAttachmentsEnabled && projectTime.attachmentCount > 0 ? "<i class='far fa-image'></i>" : ""}
						${positionAvailable ? "<i class='far fa-map-marker-alt'></i>" : ""}
						<i class='far fa-status-${toRecordedEntryStatus(projectTime.status)}'></i>
					</div>
				</div>
				${buildContextMenu().outerHTML}
			</div>

			<!-- custom fields -->
			${buildCustomFields(this.config.customFieldDefinitionsProjectTime, projectTime).outerHTML}

			<!-- validations -->
			${buildValidationsTable(subitem.validationResults.filter(v => isValidationRelevant(v)), projectTime.id).outerHTML}`;

		// add description field if description exists (in weekly mode in a separate line since the space is more limited)
		const descriptionElement = buildDescriptionElement(projectTime);
		if (descriptionElement) {
			if (this.config.mode === TimelineMode.Daily) {
				div.querySelector(".subitem-project-time-task").after(descriptionElement);
			} else {
				div.querySelector(".subitem-project-time-content").after(descriptionElement);
			}
		}

		// draw double circle dot if the subitem matches the start or end time of its parent timeline item
		if (workingTimeItem.type === WorkingTimeTimelineItemType.WORKING_TIME ||
			workingTimeItem.type === WorkingTimeTimelineItemType.WORKING_TIME_REQUEST ||
			workingTimeItem.type === WorkingTimeTimelineItemType.RUNNING) {
			// draw ⊙ dot if start times are equal (and is the first element of the wt container - to display non-duration subitems in between elements correctly)
			if (subitem.start === workingTimeItem.start && subitem === workingTimeItem.subitems[0]) {
				div.querySelector(".times").classList.add("times-no-start");

				// in ascending order, the last project-time tile touches the bottom of its parent (item) border so margin needs to be adapted for a correct spacing
				if (!this.config.timelineSortDescending) {
					div.classList.add("subitem-no-end");
				}
			}

			// draw ⊙ dot if end times are equal (and is the last element of the wt container - to display non-duration subitems in between elements correctly)
			if (subitem.end === workingTimeItem.end && subitem === workingTimeItem.subitems[workingTimeItem.subitems.length - 1]) {
				div.querySelector(".times").classList.add("times-no-end");

				// in descending order, the first project-time tile is at the top so margin needs to be adapted a correct spacing
				if (this.config.timelineSortDescending) {
					div.classList.add("subitem-no-end");
				}
			}
		}
		const contextMenu = new TimelineContextMenu<ProjectTime>();
		if (this.config.recordingFunctionsActive) {
			contextMenu
				.addMenuEntry({
					label: messages.withArguments("timeline.resume", projectTime.task.name),
					show: projectTime =>
						!showProjectTimeResumeBothMessage(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime) &&
						isProjectTimeResumable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => {
						resumeProjectTime(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime)
							?.done(() => this.dispatchEvent(new CustomEvent(EVENT_PROJECT_TIME_RESUMED, {bubbles: true})));
					}
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.resume.BOTH", this.content.workingTimeItem.workingTime?.workingTimeType.name, projectTime.task.name),
					show: projectTime =>
						showProjectTimeResumeBothMessage(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime) &&
						isProjectTimeResumable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => {
						resumeProjectTime(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime)
							?.done(() => this.dispatchEvent(new CustomEvent(EVENT_PROJECT_TIME_RESUMED, {bubbles: true})));
					}
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.start", projectTime.task.name),
					show: () => showProjectTimeQuickSwitchMessage("timeline.quick.start", this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => this.onQuickSwitchProjectTimeClicked(projectTime)
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.switch", projectTime.task.name),
					show: () => showProjectTimeQuickSwitchMessage("timeline.quick.switch", this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => {
						this.onQuickSwitchProjectTimeClicked(projectTime)
					}
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.start.BOTH", this.content.workingTimeItem.workingTime?.workingTimeType.name, projectTime.task.name),
					show: () => showProjectTimeQuickSwitchMessage("timeline.quick.start.BOTH", this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => this.onQuickSwitchProjectTimeClicked(projectTime)
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.switch.start.BOTH", this.content.workingTimeItem.workingTime?.workingTimeType.name, projectTime.task.name),
					show: () => showProjectTimeQuickSwitchMessage("timeline.quick.switch.start.BOTH", this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					action: projectTime => this.onQuickSwitchProjectTimeClicked(projectTime)
				})
				.addDivider();
		}
		contextMenu
			.addMenuEntry({
				label: "timeline.open",
				action: projectTime => this.onShowProjectTimeClicked({
					projectTime: projectTime,
					validationResults: subitem.validationResults
				})
			})
			.addMenuEntry({
				label: "timeline.edit",
				active: () => isProjectTimeEditable(projectTime),
				action: () => this.onEditProjectTimeClicked({projectTime: projectTime, validationResults: subitem.validationResults})
			})
			.addMenuEntry({
				label: "report.duplicate",
				active: () => isProjectTimeDuplicateable(),
				action: projectTime => this.onDuplicateProjectTimeClicked({
					projectTime: projectTime,
					validationResults: subitem.validationResults
				})
			})
			.addMenuEntry({
				label: "report.delete",
				active: projectTime => isProjectTimeDeletable(projectTime),
				action: projectTime => {
					deleteProjectTime(projectTime)
						.done(() => this.dispatchEvent(new CustomEvent(EVENT_PROJECT_TIME_DELETED, {bubbles: true})));
				}
			})
			.addDivider()
			.addMenuEntry({
				label: "report.showTaskDetails",
				action: projectTime => this.onShowTaskDetailsClicked(projectTime)
			});

		const projectTimeStatusNumber = toRecordedEntryStatus(projectTime.status);

		// add divider if at least one status context menu entry is shown
		// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
		if (this.config.projectTimeStatusNumbers.some(statusNumber => shouldAddStatusNumber(statusNumber, projectTimeStatusNumber, false))) {
			contextMenu.addDivider();
		}

		for (const statusNumber of this.config.projectTimeStatusNumbers) {
			const statusName = messages["reports.filter.status." + statusNumber];
			const title = messages.withArguments("report.setStatus", statusName);

			contextMenu.addMenuEntry({
				label: title,
				// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
				show: () => shouldAddStatusNumber(statusNumber, projectTimeStatusNumber, false),
				action: projectTime => {
					setProjectTimeStatus(projectTime, statusNumber)
						.done((result: ProjectTimeBulkOperationResult) => {
							if (!result.allUpdated) {
								showProjectTimeBulkResult(result, "report.setStatus.");
							}
							if (result.updated > 0) {
								this.onStatusUpdated(statusName);
							}
						});
				},
				active: projectTime => isProjectTimeStatusNumberEnabled(projectTime, statusNumber)
			});
		}

		contextMenu.bindToElement(div, projectTime);

		// click triggers event to open edit record dialog
		div.addEventListener("click", (e) => {
			if (e.target instanceof HTMLElement) {
				// check if the click event is not a context menu action e.g. resume (inside dropdown menu)
				if (!e.target.closest(".dropdown-menu")) {
					this.onShowProjectTimeClicked({projectTime: projectTime, validationResults: subitem.validationResults})
				}
			}
		});

		return div;
	}

	/**
	 * Creates UI element for a gap
	 * These are displayed inside working time items that are minute based (day-based items are displayed separately TIMR-7825)
	 */
	private buildGapItem(subitem: ProjectTimeTimelineItem): HTMLDivElement {
		const {subitems} = this.content;
		const {workingTimeItem} = this.content;
		const div = document.createElement("div");

		// working time type and category
		const workingTimeType = this.getWorkingTimeType(workingTimeItem);
		const isAttendanceTime = workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;

		// escape fields that contain modifiable text to prevent XSS
		const escapedWorkingTimeTypeName = htmlEscape(workingTimeType.name);

		// show gap info if containing wt time item is attendance time or has at least one pt/running pt
		if (isAttendanceTime || subitems.some(s => s.type === ProjectTimeTimelineItemType.PROJECT_TIME || s.type === ProjectTimeTimelineItemType.RUNNING)) {
			// show gap info if parent wt exists and is minute based (running working time is automatically minute based) or has at least one pt/running pt
			div.classList.add("subitem-gap");

			if (!isAttendanceTime) {
				// if the containing item is a non-attendance time we just show the name of the working time type (doesn't really make sense, but we need to cover this case)
				div.classList.add("subitem-not-clickable");
				div.innerHTML = `<div class="no-padding-left">${escapedWorkingTimeTypeName}</div>`;
				div.addEventListener("click", e => e.stopPropagation()); // prevents triggering the click event of the parent container for the working time dialog
			} else {
				// project times may only be added if date time change is allowed
				const isAddProjectTimeAllowed = isProjectTimeDateTimeChangeAllowed(globals.isGroupLeader);

				// show clickable "Add Project Time" gap which opens dialog on click
				div.innerHTML = `<div class="btn btn-link btn-link-primary btn-xs no-padding-left">
								<i class="fal fa-plus position-left"></i> ${messages["timeline.workingTime.addProjectTime"]}
							</div>
							<div class="duration-container">${formatDuration(subitem.duration)}</div>`;

				// disabled styling if it is not allowed to add times
				if (!isAddProjectTimeAllowed) {
					div.classList.add("subitem-not-clickable");
					div.querySelector(".btn").classList.add("disabled");
				}

				// add click listener to open add project time (only if working time item element is no break time) and it's allowed to add record (date time changeable allowed)
				div.addEventListener("click", () => {
					// only open dialog if it is allowed, otherwise nothing happens (button is disabled)
					if (isAddProjectTimeAllowed) {
						// if parent item is running working time and the most recent (last) subitem is clicked, now will be used as suggested end time
						const useNowAsEnd = this.content.workingTimeItem.type === WorkingTimeTimelineItemType.RUNNING && this.content.subitems[this.content.subitems.length - 1] === subitem;
						this.onAddProjectTimeClicked(subitem.start, subitem.end, useNowAsEnd);
					}
				});
			}
		}
		return div;
	}

	/**
	 * Creates UI element for overlap
	 */
	private buildOverlapItem(): HTMLDivElement {
		const div = document.createElement("div");
		div.classList.add("subitem-overlap");
		div.innerHTML = `<i class="fa fa-validation-severity-MINOR position-left" title="${messages["recordingValidation.severity.MINOR"]}"></i>
						<span>${messages["validationResult.entryOverlap.projectTime"]}</span>
						<i class="fal fa-random"></i>`;

		// add listeners
		div.addEventListener("click", e => {
			// prevents triggering the click event of the parent container for the working time dialog
			e.stopPropagation();
		});

		return div;
	}

	private onQuickSwitchProjectTimeClicked(projectTime: ProjectTime): void {
		const event = new CustomEvent<QuickSwitchProjectTimeEvent>(
			EVENT_PROJECT_TIME_QUICK_SWITCH, {
				detail: {
					quickSwitchProcedure: getQuickSwitchProjectTimeProcedure(this.combinedRecordingStatus, this.content.workingTimeItem, projectTime),
					workingTime: this.content.workingTimeItem.workingTime,
					projectTime: projectTime
				},
				bubbles: true
			}
		);

		// trigger event to open the edit working dialog
		this.dispatchEvent(event);
	}

	/**
	 * Triggers custom event on add project time click
	 * This subsequently opens a dialog outside this component
	 * <p>
	 * If <code>useNowAsEnd</code> is true now time will be used instead transmitted end<p>
	 * This is used if parent of subitem element is a running wt and last subitem, to keep the suggested time up to date
	 * @param start date time string of subitem
	 * @param end date time string of subitem
	 * @param useNowAsEnd if true now time will used as end
	 * @private
	 */
	private onAddProjectTimeClicked(start: string, end: string, useNowAsEnd: boolean): void {
		const event = new CustomEvent<AddProjectTimeEvent>(EVENT_PROJECT_TIME_ADD, {
			detail: {
				start: parseISOZonedDateTime(start),
				end: useNowAsEnd ? Temporal.Now.zonedDateTimeISO() : parseISOZonedDateTime(end) // use current time as end if useNowAsEnd
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	/**
	 * Handles actions on edit project time click
	 * This subsequently opens a dialog outside this component
	 */
	private onShowProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<ShowProjectTimeEvent>(
			EVENT_PROJECT_TIME_SHOW, {
				detail: {
					projectTimeItem: projectTimeItem
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	/**
	 * Handles actions on edit project time click
	 * This subsequently opens a dialog outside this component
	 */
	private onEditProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<EditProjectTimeEvent>(
			EVENT_PROJECT_TIME_EDIT, {
				detail: {
					projectTimeItem: projectTimeItem
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	/**
	 * Handles actions on edit project time click
	 * This subsequently opens a dialog outside this component
	 */
	private onDuplicateProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<DuplicateProjectTimeEvent>(
			EVENT_PROJECT_TIME_DUPLICATE, {
				detail: {
					projectTimeItem: projectTimeItem
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	/**
	 * Handles actions on show task details click
	 * This subsequently opens a dialog outside this component
	 */
	private onShowTaskDetailsClicked(projectTime: ProjectTime | RunningProjectTime): void {
		const event = new CustomEvent<ShowTaskDetailsEvent>(
			EVENT_PROJECT_TIME_SHOW_TASK_DETAILS, {
				detail: {
					projectTime: projectTime
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	/**
	 * Handles actions on project time status update
	 * By this timeline is reloaded and a notification is shown 'Status updated to {statusName}'
	 */
	private onStatusUpdated(statusName: string): void {
		const event = new CustomEvent<StatusUpdatedEvent>(
			EVENT_PROJECT_TIME_STATUS_UPDATED, {
				detail: {
					statusName: statusName
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	/**
	 * Returns the working time type of a timeline item that can contain project-time subitems.
	 * Depending on the item type, the working time type of the running entry, recorded entry or request is supplied
	 *
	 * @param item the timeline item whose working time type is required
	 * @throws Error if the timeline item does not provide a working time type
	 */
	private getWorkingTimeType(item: WorkingTimeTimelineItem): Pick<PartialWorkingTimeType, "name" | "category"> {
		let workingTimeType: Pick<PartialWorkingTimeType, "name" | "category"> | undefined;
		switch (item.type) {
			case WorkingTimeTimelineItemType.RUNNING:
				workingTimeType = item.runningWorkingTime?.workingTimeType;
				break;
			case WorkingTimeTimelineItemType.WORKING_TIME:
				workingTimeType = item.workingTime?.workingTimeType;
				break;
			case WorkingTimeTimelineItemType.WORKING_TIME_REQUEST:
				workingTimeType = item.workingTimeRequest?.workingTimeType;
				break;
		}

		if (!workingTimeType) {
			throw new Error(`Timeline item of type ${item.type} has no working time type.`);
		}

		return workingTimeType;
	}
}
